import { FC, ReactNode, useMemo } from 'react';
import { FillingExperienceContext, FillingExperienceContextProps } from './filling-experience.context';

type FillingExperienceMainProps = FillingExperienceContextProps & {
  children: ReactNode;
};

const FillingExperienceMain: FC<FillingExperienceMainProps> = ({ children, ...contextProps }) => {
  const { documentId, experienceType, locationId, isPreviewMode, isKioskOrigin } = contextProps;
  const documentType =
    experienceType === 'public' || experienceType === 'kiosk' ? contextProps.documentType : undefined;
  const patronType = experienceType === 'kiosk' ? contextProps.patronType : undefined;

  const contextValue = useMemo<FillingExperienceContextProps>(() => {
    // For solicited experience
    if (experienceType === 'solicited') {
      return {
        experienceType,
        documentId,
        locationId,
      };
    }

    // For kiosk experience
    if (experienceType === 'kiosk') {
      if (!patronType) {
        throw new Error('Patron type is required for kiosk experience');
      }

      if (documentType !== 'form' && documentType !== 'packet') {
        throw new Error('Invalid document type for kiosk experience');
      }

      return {
        experienceType,
        documentId,
        locationId,
        patronType,
        documentType,
      };
    }

    // For public experience
    switch (documentType) {
      case 'form':
      case 'packet':
        return {
          experienceType,
          documentId,
          locationId,
          documentType,
          isPreviewMode,
          isKioskOrigin,
        };

      case 'template':
        return {
          experienceType,
          documentId,
          locationId,
          documentType,
          isPreviewMode: true, // Templates are always in preview mode
        };

      default:
        throw new Error('Invalid document type for public experience');
    }
  }, [documentId, experienceType, locationId, documentType, isPreviewMode, isKioskOrigin, patronType]);

  return <FillingExperienceContext.Provider value={contextValue}>{children}</FillingExperienceContext.Provider>;
};

export default FillingExperienceMain;
